import {
  ChangeDetectorRef,
  Component,
  Input,
  ElementRef,
  EventEmitter,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import swal from "sweetalert2";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { MatrizVersion } from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { LIMITES } from "../limites";

/** Un tramo "ETIQUETA: texto" de la descripción de una versión. */
interface BloqueDescripcion {
  etiqueta: string;
  texto: string;
}

/**
 * Hoja "Hoja de Control de Cambios": historial de versiones como línea de
 * tiempo —la más reciente arriba, marcada Vigente— con alta, edición y baja.
 *
 * Elaboró y Autorizó son obligatorios en las tres operaciones; en el Excel
 * quedaron dos versiones sin firma de autorización y nada lo impidió.
 */
@Component({
  selector: "app-control-cambios-ia",
  standalone: true,
  templateUrl: "./control-cambios-ia.component.html",
  styleUrls: ["./control-cambios-ia.component.css"],
  imports: [CommonModule, FormsModule],
})
export class ControlCambiosIAComponent implements OnInit, OnChanges {

  /**
   * Límites de captura, tomados del largo real de cada columna. Van al
   * maxlength de los campos: producción corre en modo estricto y un texto más
   * largo que su columna aborta el guardado en vez de truncarse.
   */
  lim = LIMITES;

  /** Avisa al libro que el historial cambió: la ficha muestra la vigente. */
  /**
   * De qué matriz es este control de cambios. Cada matriz lleva el historial
   * de versiones de SU documento: sin este dato, todas escribirían sobre el
   * de la matriz de IA.
   */
  @Input() idMatriz = 1;

  @Output() cambio = new EventEmitter<void>();

  /**
   * Modo lectura. El historial de versiones ES el control de cambios del
   * documento certificable: registrar, corregir o borrar una versión pide el
   * mismo perfil que cambiar la matriz. Lo decide el servidor.
   */
  puedeEditar = false;

  versiones: MatrizVersion[] = [];
  nueva: MatrizVersion = this.vacia();

  cargando = true;
  guardando = false;
  mostrarAlta = false;

  /** Id de la versión que se está editando en la tabla. */
  editandoId: number | null = null;
  enEdicion: MatrizVersion = this.vacia();

  /** Descripciones ya partidas en secciones, por id de versión. */
  private bloques: { [id: number]: BloqueDescripcion[] } = {};

  /** Versiones que se están mostrando completas. */
  private expandidas = new Set<number>();

  /** Arriba de esto la descripción es candidata a recortarse. */
  private readonly LARGO_CORTE = 300;

  /**
   * Candidatas que al medirse en pantalla caben completas: en un monitor ancho
   * 400 caracteres entran en tres renglones y recortarlas desvanecería texto
   * que sí se ve, con un "Ver todo" que no revela nada.
   */
  private sinDesborde = new Set<number>();

  @ViewChildren("descRef") private descRefs!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private service: MatrizRiesgoService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  /**
   * El libro REUSA este componente al pasar de una matriz a otra por el menu:
   * Angular solo cambia el @Input, no vuelve a construirlo ni dispara
   * ngOnInit. Sin esto la pantalla se quedaba mostrando los datos de la
   * matriz ANTERIOR, y editar o borrar ahi escribia sobre el area
   * equivocada sin que nada lo avisara: el backend lo permite, porque el
   * permiso se mide contra la matriz del renglon, no contra la que se esta
   * viendo.
   *
   * Solo se recarga cuando el id cambia de verdad, no en el primer cambio
   * (de eso ya se encarga ngOnInit).
   */
  ngOnChanges(cambios: SimpleChanges): void {
    const c = cambios["idMatriz"];
    if (c && !c.firstChange && c.previousValue !== c.currentValue) {
      this.cargar();
    }
  }

  /**
   * Para pintar: la más reciente arriba. El arreglo original queda intacto
   * porque abrirAlta() calcula el siguiente número desde el último elemento.
   */
  get historial(): MatrizVersion[] {
    return [...this.versiones].reverse();
  }

  esVigente(v: MatrizVersion): boolean {
    const ultima = this.versiones[this.versiones.length - 1];
    return !!ultima && ultima.idMatrizVersion === v.idMatrizVersion;
  }

  private vacia(): MatrizVersion {
    return { idMatriz: this.idMatriz, version: "", descripcion: "", elaboro: "", autorizo: "", fechaVersion: "" };
  }

  cargar(): void {
    this.cargando = true;
    this.cdr.detectChanges();
    this.service.versiones(this.idMatriz).subscribe({
      next: (res) => {
        this.puedeEditar = res.puedeEditar === true;
        this.versiones = res.versiones || [];
        this.analizarDescripciones();
        // Se limpia ANTES de pintar: la medición sólo vale si el elemento se
        // renderea con el recorte puesto. Sin esto, uno que quedó sin recorte
        // de una carga anterior mediría "cabe" aunque ya no quepa.
        this.sinDesborde.clear();
        this.cargando = false;
        this.cdr.detectChanges();
        // Ya con el DOM pintado se mide qué descripciones desbordan de verdad.
        setTimeout(() => this.medirDesbordes());
      },
      error: (e) => {
        console.error("Error al consultar el control de cambios", e);
        this.cargando = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Quita el recorte a las candidatas que caben completas. Se decide midiendo
   * el elemento y no contando caracteres, porque cuántos renglones ocupa un
   * texto depende del ancho de la pantalla.
   */
  private medirDesbordes(): void {
    if (!this.descRefs) {
      return;
    }
    this.descRefs.forEach((ref) => {
      const el = ref.nativeElement;
      const id = Number(el.getAttribute("data-id"));
      if (id && el.scrollHeight <= el.clientHeight + 1) {
        this.sinDesborde.add(id);
      }
    });
    this.cdr.detectChanges();
  }

  /* ---------- Descripciones largas: se contraen y se expanden ---------- */

  /**
   * Se analiza una sola vez al cargar y no en cada ciclo de detección de
   * cambios: son expresiones regulares sobre textos de miles de caracteres.
   */
  private analizarDescripciones(): void {
    this.bloques = {};
    this.expandidas.clear();
    this.versiones.forEach((v) => {
      if (v.idMatrizVersion != null) {
        this.bloques[v.idMatrizVersion] = this.partir(v.descripcion || "");
      }
    });
  }

  /**
   * Parte la descripción en tramos "ETIQUETA: texto" cuando viene escrita así,
   * que es como se capturaron las versiones del FOS-005.
   *
   * Si no encuentra al menos dos etiquetas devuelve el texto tal cual: no se
   * inventa estructura donde no la hay.
   */
  private partir(texto: string): BloqueDescripcion[] {
    const limpio = (texto || "").trim();
    if (!limpio) {
      return [];
    }

    // Palabras en mayúsculas seguidas de dos puntos: IDENTIFICACIÓN:,
    // RIESGOS DE PROCESO:, ALCANCE:. Se piden 3 letras para no confundir
    // siglas sueltas como IA: o QA:.
    const rx = /(?:^|\s)([A-ZÁÉÍÓÚÑ]{3,}(?: [A-ZÁÉÍÓÚÑ]{1,})*):\s+/g;
    const cortes: { inicio: number; fin: number; etiqueta: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = rx.exec(limpio)) !== null) {
      cortes.push({ inicio: m.index, fin: rx.lastIndex, etiqueta: m[1] });
    }

    if (cortes.length < 2) {
      return [{ etiqueta: "", texto: limpio }];
    }

    const partes: BloqueDescripcion[] = [];
    const preambulo = limpio.slice(0, cortes[0].inicio).trim();
    if (preambulo) {
      partes.push({ etiqueta: "", texto: preambulo });
    }
    cortes.forEach((c, i) => {
      const hasta = i + 1 < cortes.length ? cortes[i + 1].inicio : limpio.length;
      partes.push({ etiqueta: c.etiqueta, texto: limpio.slice(c.fin, hasta).trim() });
    });
    return partes;
  }

  bloquesDe(v: MatrizVersion): BloqueDescripcion[] {
    const id = v.idMatrizVersion;
    return id != null && this.bloques[id] ? this.bloques[id] : [{ etiqueta: "", texto: v.descripcion || "" }];
  }

  /** Larga por caracteres Y desbordada en pantalla: si cabe, no se recorta. */
  esLarga(v: MatrizVersion): boolean {
    if ((v.descripcion || "").length <= this.LARGO_CORTE) {
      return false;
    }
    return v.idMatrizVersion == null || !this.sinDesborde.has(v.idMatrizVersion);
  }

  estaExpandida(v: MatrizVersion): boolean {
    return v.idMatrizVersion != null && this.expandidas.has(v.idMatrizVersion);
  }

  alternarDescripcion(v: MatrizVersion): void {
    const id = v.idMatrizVersion;
    if (id == null) {
      return;
    }
    if (this.expandidas.has(id)) {
      this.expandidas.delete(id);
    } else {
      this.expandidas.add(id);
    }
  }

  /** Propone el siguiente número: 1.7 -> 1.8, 1.9 -> 1.10. */
  abrirAlta(): void {
    // Un formulario a la vez: si había una edición abierta, se cierra.
    this.cancelarEdicion();
    this.nueva = this.vacia();
    if (this.versiones.length > 0) {
      const ult = this.versiones[this.versiones.length - 1].version || "";
      const partes = ult.split(".");
      if (partes.length === 2 && !Number.isNaN(Number(partes[1]))) {
        this.nueva.version = partes[0] + "." + (Number(partes[1]) + 1);
      }
      this.nueva.elaboro = this.versiones[this.versiones.length - 1].elaboro || "";
    }
    const hoy = new Date();
    this.nueva.fechaVersion =
      hoy.getFullYear() +
      "-" +
      String(hoy.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(hoy.getDate()).padStart(2, "0");
    this.mostrarAlta = true;
  }

  cancelar(): void {
    this.mostrarAlta = false;
    this.nueva = this.vacia();
  }

  /* ---------- Edición de una versión ya registrada ---------- */

  /* ---------- Una sola captura abierta a la vez ---------- */

  /**
   * Bloquea la fila cuando hay OTRA version en edicion o cuando se esta
   * registrando una nueva. Sin esto conviven dos formularios abiertos y es
   * facil guardar el equivocado.
   */
  bloqueado(v: MatrizVersion): boolean {
    if (this.mostrarAlta) {
      return true;
    }
    return this.editandoId !== null && this.editandoId !== v.idMatrizVersion;
  }

  /** Por que esta apagado el boton. Sale como title. */
  avisoBloqueo(): string {
    if (this.mostrarAlta) {
      return "Estás registrando una versión nueva: guárdala o cancélala primero.";
    }
    if (this.editandoId === null) {
      return "";
    }
    const v = this.versiones.find((x) => x.idMatrizVersion === this.editandoId);
    const num = v && v.version ? "la versión " + v.version : "la versión abierta";
    return "Termina primero " + num + ": guárdala o cancélala.";
  }

  editar(v: MatrizVersion): void {
    if (this.bloqueado(v)) {
      return;
    }
    // Reset por si quedara algo del alta a medio llenar.
    this.cancelar();
    // Se edita sobre una copia: si el usuario cancela, la tarjeta no queda tocada.
    this.editandoId = v.idMatrizVersion || null;
    this.enEdicion = { ...v };
    if (this.enEdicion.fechaVersion) {
      const d = new Date(this.enEdicion.fechaVersion as string);
      this.enEdicion.fechaVersion =
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0");
    }
  }

  cancelarEdicion(): void {
    this.editandoId = null;
    this.enEdicion = this.vacia();
  }

  guardarEdicion(): void {
    const v = this.enEdicion;
    if (!v.version || !v.version.trim()) {
      swal.fire("Falta la versión", "Indica el número de versión.", "warning");
      return;
    }
    if (!v.descripcion || !v.descripcion.trim()) {
      swal.fire("Falta la descripción", "Describe qué cambió en esta versión.", "warning");
      return;
    }
    if (!v.elaboro || !v.elaboro.trim()) {
      swal.fire("Falta quién elaboró", "Indica el nombre de quien elaboró.", "warning");
      return;
    }
    if (!v.autorizo || !v.autorizo.trim()) {
      swal.fire("Falta quién autorizó", "Indica el nombre de quien autoriza esta versión.", "warning");
      return;
    }
    if (!v.fechaVersion) {
      swal.fire("Falta la fecha", "Indica la fecha de la versión.", "warning");
      return;
    }

    this.guardando = true;
    this.cdr.detectChanges();
    this.service.actualizarVersion(v).subscribe({
      next: (res) => {
        this.guardando = false;
        this.cdr.detectChanges();
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Error desconocido.", "warning");
          return;
        }
        this.cancelarEdicion();
        this.cargar();
        this.cambio.emit();
      },
      error: (e) => {
        console.error("Error al actualizar la versión", e);
        this.guardando = false;
        this.cdr.detectChanges();
        swal.fire("Error", "No se pudo actualizar la versión.", "error");
      },
    });
  }

  /* ---------- Confirmacion propia, centrada ---------- */

  /**
   * El confirm de la libreria se dibuja en su propio nivel y este componente
   * vive dentro de la ventana del historial, que va mas arriba: el dialogo
   * quedaba DETRAS del modal. Este se pinta dentro del mismo contenedor, asi
   * que siempre queda encima. Se cierra solo con sus botones.
   */
  confirma: {
    titulo: string;
    texto: string;
    detalle?: string;
    botonSi: string;
    accion: () => void;
  } | null = null;

  confirmarNo(): void {
    this.confirma = null;
  }

  confirmarSi(): void {
    const hacer = this.confirma ? this.confirma.accion : null;
    this.confirma = null;
    if (hacer) {
      hacer();
    }
  }

  eliminar(v: MatrizVersion): void {
    if (this.bloqueado(v)) {
      return;
    }
    const vigente = this.esVigente(v);
    this.confirma = {
      titulo: "¿Eliminar la versión " + v.version + "?",
      texto: vigente
        ? "Es la versión vigente del documento: al quitarla, la vigente pasa a ser la anterior."
        : "Se borra del historial del documento.",
      detalle: "Esto no se puede deshacer.",
      botonSi: "Sí, eliminar",
      accion: () => this.eliminarConfirmado(v),
    };
  }

  private eliminarConfirmado(v: MatrizVersion): void {
    this.service.eliminarVersion(v.idMatrizVersion!).subscribe({
      next: (res) => {
        if (!res || res.codigoError) {
          swal.fire("No se eliminó", (res && res.mensajeError) || "Error desconocido.", "warning");
          return;
        }
        this.cargar();
        this.cambio.emit();
      },
      error: () => swal.fire("Error", "No se pudo eliminar la versión.", "error"),
    });
  }

  guardar(): void {
    if (!this.nueva.version || !this.nueva.version.trim()) {
      swal.fire("Falta la versión", "Indica el número de versión.", "warning");
      return;
    }
    if (!this.nueva.descripcion || !this.nueva.descripcion.trim()) {
      swal.fire("Falta la descripción", "Describe qué cambió en esta versión.", "warning");
      return;
    }
    if (!this.nueva.elaboro || !this.nueva.elaboro.trim()) {
      swal.fire("Falta quién elaboró", "Indica el nombre de quien elaboró.", "warning");
      return;
    }
    if (!this.nueva.autorizo || !this.nueva.autorizo.trim()) {
      swal.fire(
        "Falta quién autorizó",
        "Autorizar es un acto formal: indica el nombre de quien autoriza esta versión.",
        "warning"
      );
      return;
    }
    if (!this.nueva.fechaVersion) {
      swal.fire("Falta la fecha", "Indica la fecha de la versión.", "warning");
      return;
    }

    this.guardando = true;
    this.cdr.detectChanges();
    this.service.guardarVersion(this.nueva).subscribe({
      next: (res) => {
        this.guardando = false;
        this.cdr.detectChanges();
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Error desconocido.", "warning");
          return;
        }
        this.mostrarAlta = false;
        this.nueva = this.vacia();
        this.cargar();
        this.cambio.emit();
      },
      error: (e) => {
        console.error("Error al guardar la versión", e);
        this.guardando = false;
        this.cdr.detectChanges();
        swal.fire("Error", "No se pudo guardar la versión.", "error");
      },
    });
  }
}
